import type { CountryIsoCode } from '../../../types/countryIsoCode'
import { InvoiceComplianceMode } from '../enums/invoiceComplianceMode'
import { getIsRequiredSharedCapital } from './getIsRequiredSharedCapital'
import { hasValidRegistrationNumber } from './hasValidRegistrationNumber'
import { hasValidVatNumber } from './hasValidVatNumber'
import { shouldAllowVatNumberWhenExempt } from './shouldAllowVatNumberWhenExempt'

interface GetCompanyMissingFieldsOptions {
  city: string | null | undefined
  complianceMode: InvoiceComplianceMode
  countryId?: Uppercase<CountryIsoCode> | null
  isSiretPendingAssignment?: boolean
  iban: string | undefined
  isVatExempted: boolean
  registrationNo: string | null | undefined
  street: string | null | undefined
  vatNo: string | null | undefined
  zipCode: string | null | undefined
  legalStatus?: string
  sharedCapital?: number
}

interface GetCompanyMissingFieldsResult {
  isMissingAddress: boolean
  isMissingCity: boolean
  isMissingDetails: boolean
  isMissingPaymentInfo: boolean
  isMissingStreet: boolean
  isMissingTaxInfo: boolean
  isMissingVatNo: boolean
  isMissingRegistrationNo: boolean
  isMissingZipCode: boolean
  isMissingLegalInfo: boolean
  isMissingLegalStatus: boolean
  isMissingSharedCapital: boolean
}

export const getCompanyMissingFields = ({
  city,
  complianceMode,
  countryId,
  iban,
  isSiretPendingAssignment,
  isVatExempted,
  registrationNo,
  street,
  vatNo,
  zipCode,
  sharedCapital,
  legalStatus,
}: GetCompanyMissingFieldsOptions): GetCompanyMissingFieldsResult => {
  const isMissingCity = !city
  const isMissingZipCode = !zipCode
  const isMissingStreet = !street

  const isMissingShortAddress = isMissingCity || isMissingZipCode
  const isMissingFullAddress = isMissingShortAddress || isMissingStreet

  const isMissingLegalStatus = !legalStatus
  const isMissingSharedCapital = getIsRequiredSharedCapital({ legalStatus, sharedCapital })
  const isMissingLegalInfo = isMissingLegalStatus || isMissingSharedCapital

  // Format checks mirror CompanyTaxInfoSection validation: users can arrive with invalid VAT/registration numbers from the migration service
  const isVatNumberValid = hasValidVatNumber(vatNo, countryId)
  const isVatNumberRequired = !isVatExempted || (!!vatNo && shouldAllowVatNumberWhenExempt(countryId))
  const isRegistrationNumberValid = hasValidRegistrationNumber(registrationNo, countryId)

  switch (complianceMode) {
    case InvoiceComplianceMode.French: {
      const isMissingRegistrationNo = isSiretPendingAssignment ? false : !isRegistrationNumberValid
      const isMissingVatNo = isVatNumberRequired && !isVatNumberValid
      const isMissingTaxInfo = isMissingVatNo || isMissingRegistrationNo

      return {
        isMissingAddress: isMissingFullAddress,
        isMissingCity,
        isMissingPaymentInfo: false,
        isMissingRegistrationNo,
        isMissingStreet,
        isMissingTaxInfo,
        isMissingVatNo,
        isMissingZipCode,
        isMissingDetails: isMissingFullAddress || isMissingTaxInfo || isMissingLegalInfo,
        isMissingLegalInfo,
        isMissingLegalStatus,
        isMissingSharedCapital,
      }
    }
    case InvoiceComplianceMode.Default: {
      const isMissingRegistrationNo = !isRegistrationNumberValid
      const isMissingVatNo = isVatNumberRequired && !isVatNumberValid
      const hasTaxIdentifierSatisfied = !isVatNumberRequired || isVatNumberValid || isRegistrationNumberValid
      const hasAnyTaxNumberFormatError =
        (!!vatNo && !isVatNumberValid) || (!!registrationNo && !isRegistrationNumberValid)
      const isMissingTaxInfo = !hasTaxIdentifierSatisfied || hasAnyTaxNumberFormatError
      const isMissingIban = !iban

      return {
        isMissingAddress: isMissingShortAddress,
        isMissingCity,
        isMissingPaymentInfo: isMissingIban,
        isMissingRegistrationNo,
        isMissingStreet: false,
        isMissingTaxInfo,
        isMissingVatNo,
        isMissingZipCode,
        isMissingDetails: isMissingShortAddress || isMissingTaxInfo || isMissingIban,
        isMissingLegalInfo: false,
        isMissingLegalStatus: false,
        isMissingSharedCapital: false,
      }
    }
  }
}
